/**
 * EditOpportunityFields.jsx - the labelled Section wrapper and the renderRow
 * factory every field in the editor goes through (text, number, select,
 * reference picklist, owner, searchable customer, several engineers).
 */
import { useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import { GAT_ACCENT, computedSt, inputSt, labelSt, requiredSt, rowSt } from './editOpportunityConstants';
import { settleCustomer } from './editOpportunityForm';
import EngineerMultiSelect from '../forms/EngineerMultiSelect';
import RefPicker from '../forms/RefPicker';

// Module-level so it's a STABLE component type. Defining this inside
// EditOpportunityModal made React remount the whole section (and its inputs) on
// every keystroke, so focus was lost after one character — the "can't type more
// than one word" bug. Keep section/row components out of render bodies.
export function Section({ title, icon, accent = GAT_ACCENT, children }) {
  return (
    <div
      style={{
        minWidth: 0,
        background: '#fff',
        border: '1px solid #E5E9DD',
        borderRadius: 10,
        padding: '12px 16px',
        boxShadow: '0 1px 2px rgba(27,94,32,0.04)',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: `2px solid ${accent}` }}
      >
        <Icon name={icon} size={13} color={accent} />
        <span style={{ fontSize: 11, fontWeight: 800, color: accent, letterSpacing: 1.6, textTransform: 'uppercase' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Settle a typed customer name against the list (the rules are below, on
 * CustomerSearch). `prev` is what the field held when it was entered.
 * Resolves { value, ok }: value is what the field should now hold; ok false
 * means an unknown name was not added, so value is `prev` again.
 */
async function resolveCustomer(typed, prev, options, addRef) {
  const s = settleCustomer(typed, options);
  if (s.kind === 'blank') return { value: '', ok: true };
  if (s.kind === 'match') return { value: s.name, ok: true };
  if (s.name === settleCustomer(prev).name) return { value: s.name, ok: true };
  if (!window.confirm(`Add ${s.name} as a new customer?`)) return { value: prev, ok: false };
  const clean = await addRef('customer', s.name);
  return { value: clean || s.name, ok: true };
}

/**
 * The Customer field (r49): type to search the customer list instead of
 * scrolling a long dropdown (a datalist, so the browser does the filtering).
 * Leaving the field settles the name (settleCustomer): an exact match, in any
 * case, takes the list's spelling; a name the list does not have asks "Add
 * <name> as a new customer?" - yes adds it through addRef('customer'), no puts
 * back what the field held when it was entered. A name that was already on
 * the record and was not touched is left alone, so opening an old record
 * never asks about it.
 *
 * Save settles it too (r49 fix F6): in Chrome the confirm opened by the blur
 * of a click on Save swallowed that click, so Save took two clicks. The
 * footer's Save keeps the focus here on mousedown, so no blur, and calls
 * handle.current.flush() first, which settles a name still being typed.
 * Module scope (a stable component type), like Section, so typing keeps focus.
 *
 * The suggestions are its own list under the box, not the browser's datalist
 * (10/8/26), so each name can carry a red x that takes it off the customer
 * list (onRemove, passed only to people who may change the lists).
 */
export function CustomerSearch({ value, onChange, options, addRef, onRemove, placeholder, handle }) {
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false); // filter only once something is typed
  const before = useRef(value); // the value when the field was entered
  const dirty = useRef(false); // typed in since it was last settled
  const running = useRef(null); // a settle still waiting on addRef
  const settle = (typed) => {
    dirty.current = false;
    const run = resolveCustomer(typed, before.current || '', options, addRef)
      .then((r) => {
        if (r.value !== typed) onChange(r.value);
        // settled: leaving the field or saving later does not ask again
        if (r.ok) before.current = r.value;
        return r;
      })
      .finally(() => {
        if (running.current === run) running.current = null;
      });
    running.current = run;
    return run;
  };
  // For Save: null when there is nothing to settle (so Save stays
  // synchronous), else a promise of { ok, value } - ok false: the add was
  // declined and the field is back to what it held.
  if (handle) handle.current = { flush: () => (dirty.current ? settle(value) : running.current) };
  const typed = String(value || '')
    .trim()
    .toLowerCase();
  const shown = open ? (typing && typed ? options.filter((n) => n.toLowerCase().includes(typed)) : options) : [];
  const remove = async (n) => {
    if (
      !window.confirm(`Remove "${n}" from the customer list?

Records that already use it keep it. Typing it again adds it back.`)
    )
      return;
    await onRemove(n);
  };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <input
        style={{ ...inputSt, width: '100%' }}
        value={value}
        aria-label="Customer"
        autoComplete="off"
        aria-autocomplete="list"
        placeholder={placeholder}
        onFocus={() => {
          before.current = value;
          setOpen(true);
          setTyping(false);
        }}
        onChange={(e) => {
          dirty.current = true;
          setOpen(true);
          setTyping(true);
          onChange(e.target.value);
        }}
        onBlur={(e) => {
          setOpen(false);
          settle(e.target.value);
        }}
      />
      {shown.length > 0 && (
        <div
          role="listbox"
          aria-label="Customers"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 60,
            marginTop: 2,
            background: '#fff',
            border: '1px solid #D8DED4',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {shown.map((n) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #F3F3F0' }}>
              {/* mousedown, not click: the box keeps the focus, so leaving it settles once */}
              <div
                role="option"
                aria-selected={n === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  dirty.current = true;
                  onChange(n);
                  setOpen(false);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '7px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {n}
              </div>
              {onRemove && (
                <button
                  type="button"
                  aria-label={`Remove ${n} from the list`}
                  title={`Remove ${n} from the customer list`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    remove(n);
                  }}
                  style={{
                    flexShrink: 0,
                    background: 'none',
                    border: 'none',
                    color: '#C62828',
                    fontSize: 15,
                    lineHeight: 1,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// GAT-styled input / row primitives — module scope so they're shared by the
// render helpers below. Pure constants: hoisting them out of the modal's
// render body changes nothing visually.

// removeRef: the red x on a list item, passed only to people who may change the lists.
// `missing`: the keys of required fields a Save found blank - their rows turn
// red until filled. Every row carries data-field=<key>, so a Save that stops on
// one can scroll to it.
const missingRowSt = { background: '#FFF5F5', boxShadow: 'inset 3px 0 0 #B71C1C', paddingLeft: 8 };
export const createRenderRow =
  ({
    f,
    set,
    setF,
    setNum,
    allAccounts,
    users,
    resolveOwnerName,
    refLists,
    addRef,
    removeRef,
    customerField,
    missing = {},
    onNextQuote = null,
  }) =>
  (label, key, opts = {}) => {
    const { required, kind = 'text', placeholder = '-' } = opts;
    const bad = !!missing[key];
    return (
      <div key={key} data-field={key} style={bad ? { ...rowSt, ...missingRowSt } : rowSt}>
        <div style={bad ? { ...labelSt, color: '#B71C1C' } : labelSt}>
          {label}
          {required && <span style={requiredSt}>*</span>}
          {bad && <div style={{ fontSize: 9.5, fontWeight: 700, marginTop: 2 }}>Required</div>}
        </div>
        {kind === 'customer' ? (
          // the canonical customers; before that list is seeded, the account names
          <CustomerSearch
            value={f[key]}
            onChange={(v) => setF((p) => ({ ...p, [key]: v }))}
            options={(refLists.customer || []).length ? refLists.customer : (allAccounts || []).map((a) => a.name).filter(Boolean)}
            addRef={addRef}
            onRemove={removeRef ? (n) => removeRef('customer', n) : undefined}
            placeholder="Type to search customers…"
            handle={customerField}
          />
        ) : kind === 'engineers' ? (
          // r49: several engineers, stored comma-separated in one field exactly
          // as the Projects form stores them (forms/EngineerMultiSelect)
          <div style={{ flex: 1, minWidth: 0 }}>
            <EngineerMultiSelect
              value={f[key]}
              onChange={(v) => setF((p) => ({ ...p, [key]: v }))}
              options={refLists[opts.refKind || 'engineer'] || []}
              addRef={addRef}
              onRemove={removeRef ? (n) => removeRef(opts.refKind || 'engineer', n) : undefined}
              selectStyle={{ ...inputSt, width: '100%' }}
            />
          </div>
        ) : kind === 'date' ? (
          <input type="date" style={inputSt} value={f[key] || ''} onChange={set(key)} />
        ) : kind === 'textarea' ? (
          <textarea
            style={{ ...inputSt, minHeight: 60, resize: 'vertical' }}
            value={f[key]}
            onChange={set(key)}
            placeholder={placeholder}
          />
        ) : kind === 'grow' ? (
          // wraps and grows with what it holds (Invoice #'s): a long list used
          // to run off the end of a one-line box and looked cut off (10/8/26)
          <textarea
            style={{ ...inputSt, resize: 'vertical', minHeight: 36, lineHeight: 1.5 }}
            rows={Math.min(4, Math.max(1, Math.ceil(String(f[key] || '').length / 38)))}
            value={f[key]}
            onChange={set(key)}
            placeholder={placeholder}
          />
        ) : kind === 'quote' ? (
          // the quote number, with "Next #": the server's next free number (one
          // company-wide series, main + GLOBAL KSA) - as the Projects form had
          <div style={{ flex: 1, display: 'flex', gap: 6, minWidth: 0 }}>
            <input style={{ ...inputSt, minWidth: 0 }} value={f[key]} onChange={set(key)} placeholder={placeholder} />
            {onNextQuote && (
              <button
                type="button"
                onClick={onNextQuote}
                title="Fill in the next free quote number"
                style={{
                  flexShrink: 0,
                  background: '#E8F5E9',
                  color: '#1B5E20',
                  border: '1px solid #C8E6C9',
                  borderRadius: 6,
                  padding: '0 12px',
                  fontWeight: 700,
                  fontSize: 10.5,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                  whiteSpace: 'nowrap',
                }}
              >
                Next #
              </button>
            )}
          </div>
        ) : kind === 'computed' ? (
          // worked out from other fields, never typed (opts.value is the text shown)
          <input
            style={computedSt}
            value={opts.value == null ? '' : opts.value}
            readOnly
            tabIndex={-1}
            title={opts.title}
            placeholder={placeholder}
          />
        ) : kind === 'select' ? (
          // an option is its value, or a [value, label] pair (Active / Closed stores 'YES')
          (() => {
            const pairs = opts.options.map((o) => (Array.isArray(o) ? o : [o === '-' ? '' : o, o]));
            return (
              <select style={inputSt} value={f[key]} onChange={set(key)}>
                {pairs.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
                {/* A value already on the row that is no longer offered still has to
                  be selectable, or the browser shows the first option instead and
                  the next save silently rewrites the field. Bites whenever a
                  picklist is narrowed - Division going to Services/Solutions left
                  29 "Staffing" rows in exactly this position. */}
                {f[key] && !pairs.some(([v]) => v === f[key]) && <option value={f[key]}>{f[key]} (legacy)</option>}
              </select>
            );
          })()
        ) : kind === 'owner' ? (
          <select style={inputSt} value={f[key]} onChange={set(key)}>
            <option value="">— Unassigned</option>
            {users.map((u) => (
              <option key={u.email} value={u.email}>
                {u.name || u.email}
              </option>
            ))}
            {/* keep a legacy/free-text owner selectable even if not in the registry */}
            {f[key] && !users.some((u) => (u.email || '').toLowerCase() === f[key].toLowerCase()) && (
              <option value={f[key]}>{resolveOwnerName(f[key])}</option>
            )}
          </select>
        ) : kind === 'ref' && (refLists[opts.refKind] || []).length > 0 ? (
          // Canonical dropdown: values come from the refs registry so names
          // stay uniform. "＋ Add new…" registers a canonical entry inline.
          <RefPicker
            label={label}
            style={inputSt}
            value={f[key]}
            options={refLists[opts.refKind] || []}
            onChange={(v) => setF((p) => ({ ...p, [key]: v }))}
            onAddNew={async () => {
              const entered = window.prompt(`New ${label}:`);
              const clean = entered ? await addRef(opts.refKind, entered) : null;
              if (clean) setF((p) => ({ ...p, [key]: clean }));
            }}
            onRemove={removeRef ? (n) => removeRef(opts.refKind, n) : undefined}
          />
        ) : kind === 'num' ? (
          <input style={inputSt} value={f[key]} onChange={setNum(key)} placeholder={placeholder} />
        ) : (
          <input style={inputSt} value={f[key]} onChange={set(key)} placeholder={placeholder} />
        )}
      </div>
    );
  };

/* Hero header (title, owner avatar, meta cards, sales stages, tab bar).
   Plain render function called as {renderHeroHeader(...)} so the element
   tree is identical to the previous inline JSX — no new component layer. */
